<template>
  <AppLayout>
    <div class="transaction email-logs-page">
      <section id="transaction-products">
        <div class="container email-logs-container">

          <div class="d-flex align-items-center justify-content-between mb-3">
            <h6 class="fw-bold mb-0">Email Logs</h6>
            <span class="text-muted" style="font-size:11px;">Track all outgoing emails related to tenders</span>
          </div>

          <!-- Filters -->
          <div class="email-logs-filters">
            <div class="email-logs-filters-row">
              <div class="email-logs-filter-group">
                <label class="email-logs-filter-label">Search</label>
                <input
                  v-model="filters.tenderSearch"
                  @input="debouncedFetch"
                  type="text"
                  class="form-control form-control-sm"
                  placeholder="Tender name or ref #"
                />
              </div>
              <div class="email-logs-filter-group">
                <label class="email-logs-filter-label">Type</label>
                <select v-model="filters.type" @change="fetchLogs" class="form-select form-select-sm">
                  <option value="">All Types</option>
                  <option value="TENDER_INVITATION">Tender Invitation</option>
                  <option value="AWARD_NOTIFICATION">Award Notification</option>
                  <option value="REJECTION_NOTIFICATION">Rejection Notification</option>
                  <option value="CONTRACT_EXPIRY_30_DAYS">Contract Expiry — 30 Days</option>
                  <option value="CONTRACT_EXPIRY_14_DAYS">Contract Expiry — 14 Days</option>
                  <option value="CONTRACT_EXPIRY_7_DAYS">Contract Expiry — 7 Days</option>
                  <option value="CONTRACT_EXPIRY_1_DAY">Contract Expiry — 1 Day</option>
                  <option value="CONTRACT_EXPIRY_EXPIRED">Contract Expired</option>
                </select>
              </div>
              <div class="email-logs-filter-group">
                <label class="email-logs-filter-label">Status</label>
                <select v-model="filters.status" @change="fetchLogs" class="form-select form-select-sm">
                  <option value="">All Statuses</option>
                  <option value="SENT">Sent</option>
                  <option value="FAILED">Failed</option>
                </select>
              </div>
              <!--<div class="email-logs-filter-actions">
                <button class="btn btn-sm btn-ghost" @click="clearFilters">
                  <i class="fas fa-times me-1"></i>Clear
                </button>
              </div>-->
            </div>
            <!--<div class="email-logs-stats">
              <span class="badge badge-subtle-total">Total: {{ total }}</span>
              <span class="badge badge-subtle-sent">Sent: {{ sentCount }}</span>
              <span class="badge badge-subtle-failed">Failed: {{ failedCount }}</span>
            </div>-->
          </div>

          <!-- Table card -->
          <div class="email-logs-card">
            <div class="email-logs-table-wrapper">
              <div v-if="loading" class="email-logs-loading">
                <i class="fas fa-spinner fa-spin me-2"></i>Loading...
              </div>
              <table
              v-else
              id="emailLogsTable"
              ref="emailLogsTable"
              class="table table-sm table-hover mb-0"
              style="font-size:11px;"
              @click="onTableClick"
            >
              <thead class="table-light">
                <tr>
                  <th>#</th>
                  <th>Sent At</th>
                  <th>Tender</th>
                  <th>Recipient</th>
                  <th>Subject</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="log in logs" :key="log.id" :data-id="log.id">
                  <td class="text-muted">{{ log.id }}</td>
                  <td>{{ formatDate(log.sentAt) }}</td>
                  <td>
                    <span v-if="log.tender" style="font-size:11px;">
                      {{ log.tender.title }}<br/>
                      <span class="text-muted">{{ log.tender.referenceNumber }}</span>
                    </span>
                    <span v-else class="text-muted">—</span>
                  </td>
                  <td style="max-width:200px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">{{ log.recipient }}</td>
                  <td style="max-width:240px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;" :title="log.subject">{{ log.subject }}</td>
                  <td><span class="badge" :style="typeBadgeStyle(log.type)" style="font-size:10px;">{{ formatType(log.type) }}</span></td>
                  <td>
                    <span :class="log.status === 'SENT' ? 'badge badge-subtle-sent' : 'badge badge-subtle-failed'" style="font-size:11px;">
                      {{ log.status }}
                    </span>
                    <div v-if="log.status === 'FAILED' && log.errorMessage" class="text-danger mt-1" style="font-size:11px;">{{ log.errorMessage }}</div>
                  </td>
                  <td>
                    <button class="btn btn-xs btn-ghost me-1" title="Preview email" :data-id="log.id" data-action="preview">
                      <i class="fas fa-eye"></i>
                    </button>
                    <button
                      class="btn btn-xs btn-ghost"
                      title="Resend email"
                      :disabled="resendingId === log.id"
                      :data-id="log.id"
                      data-action="resend"
                    >
                      <i :class="resendingId === log.id ? 'fas fa-spinner fa-spin' : 'fas fa-paper-plane'"></i>
                    </button>
                  </td>
                </tr>
                <tr v-if="!loading && !logs.length">
                  <td colspan="8" class="text-center text-muted py-4">No email logs found.</td>
                </tr>
              </tbody>
            </table>
            </div>

            <!-- Pagination (server-side) -->
          <div class="d-flex justify-content-between align-items-center mt-3" style="font-size:11px;">
            <span class="text-muted">Page {{ currentPage }} of {{ totalPages }}</span>
            <div class="d-flex gap-2">
              <button class="btn btn-sm btn-style-2" :disabled="currentPage <= 1" @click="goToPage(currentPage - 1)">
                <i class="fas fa-chevron-left"></i>
              </button>
              <button
                v-for="p in pageRange"
                :key="p"
                class="btn btn-sm"
                :class="p === currentPage ? 'btn-primary' : 'btn-style-2'"
                @click="goToPage(p)"
              >{{ p }}</button>
              <button class="btn btn-sm btn-style-2" :disabled="currentPage >= totalPages" @click="goToPage(currentPage + 1)">
                <i class="fas fa-chevron-right"></i>
              </button>
            </div>
          </div>
          </div>

        </div>
      </section>
    </div>

    <!-- Email Preview Modal -->
    <div v-if="previewHtml" class="modal-overlay" @click.self="previewHtml = null">
      <div class="modal-content" style="max-width:720px;max-height:85vh;display:flex;flex-direction:column;">
        <div class="modal-header">
          <h5 class="email-preview-title">Email Preview — {{ previewSubject }}</h5>
          <button class="close-btn" @click="previewHtml = null">&times;</button>
        </div>
        <div style="flex:1;overflow:auto;padding:16px;">
          <!-- eslint-disable-next-line vue/no-v-html -->
          <iframe
            :srcdoc="previewHtml"
            style="width:100%;min-height:500px;border:1px solid #e5e7eb;border-radius:4px;"
            sandbox="allow-same-origin"
          ></iframe>
        </div>
        <div class="modal-footer" style="padding:11px 16px;border-top:1px solid #e5e7eb;display:flex;justify-content:flex-end;gap:8px;">
          <button class="btn btn-sm btn-style-2" @click="previewHtml = null">Close</button>
          <button class="btn btn-sm btn-primary" @click="resend(previewLog_)">
            <i class="fas fa-paper-plane me-1"></i>Resend
          </button>
        </div>
      </div>
    </div>

  </AppLayout>
</template>

<script>
/* eslint-disable */
import { ref, reactive, computed, onMounted, onBeforeUnmount, nextTick } from 'vue'
import $ from 'jquery'
import 'datatables.net-vue3'
import 'datatables.net-bs5'
import 'datatables.net-buttons-bs5'
import 'datatables.net-responsive-bs5'
import 'jszip'
import 'datatables.net-buttons/js/buttons.html5.js'
import 'datatables.net-buttons/js/buttons.colVis.js'
import 'datatables.net-buttons/js/buttons.print.js'
import AppLayout from '../../components/AppLayout.vue'

const API_BASE = (process.env.VUE_APP_URL || 'http://localhost:3000').replace(/\/+$/, '')
function getToken() {
  try {
    const raw = localStorage.getItem('access_token')
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return typeof parsed === 'string' ? parsed : (parsed?.access_token ?? parsed?.token ?? null)
  } catch (e) {
    return localStorage.getItem('access_token')
  }
}

const debounce = (fn, ms) => {
  let t
  return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms) }
}

export default {
  name: 'EmailLogs',
  components: { AppLayout },
  setup() {
    const logs        = ref([])
    const loading     = ref(false)
    const total       = ref(0)
    const totalPages  = ref(1)
    const currentPage = ref(1)
    const sentCount   = ref(0)
    const failedCount = ref(0)
    const resendingId = ref(null)
    const previewHtml    = ref(null)
    const previewSubject = ref('')
    const previewLog_    = ref(null)
    const emailLogsTable  = ref(null)
    let dataTable = null

    const filters = reactive({
      tenderSearch: '',
      type:         '',
      status:       '',
    })

    const pageRange = computed(() => {
      const range = []
      const start = Math.max(1, currentPage.value - 2)
      const end   = Math.min(totalPages.value, start + 4)
      for (let i = start; i <= end; i++) range.push(i)
      return range
    })

    const fetchLogs = async (page = currentPage.value) => {
      loading.value = true
      destroyDataTable()
      try {
        const params = new URLSearchParams({ page, limit: 30 })
        if (filters.tenderSearch) params.set('tenderSearch', filters.tenderSearch)
        if (filters.type)         params.set('type',         filters.type)
        if (filters.status)       params.set('status',       filters.status)

        const token = getToken()
        const res  = await fetch(`${API_BASE}/commication/email-logs?${params}`, {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        })
        const data = await res.json()
        if (!res.ok) {
          console.error('[EmailLogs] API error:', res.status, data)
          if (res.status === 401) {
            alert('Session expired. Please log in again.')
            window.location.href = '/login'
            return
          }
          throw new Error(data?.message || `Request failed (${res.status})`)
        }
        logs.value        = data.data        ?? []
        total.value       = data.total       ?? 0
        totalPages.value  = data.totalPages  ?? 1
        currentPage.value = data.page        ?? page
        sentCount.value   = data.sentCount   ?? logs.value.filter(l => l.status === 'SENT').length
        failedCount.value = data.failedCount ?? logs.value.filter(l => l.status === 'FAILED').length
      } catch (e) {
        console.error('[EmailLogs]', e)
        logs.value = []
      } finally {
        loading.value = false
        nextTick(() => setTimeout(() => reinitDataTable(), 50))
      }
    }

    const reinitDataTable = () => {
      try {
        if (dataTable && emailLogsTable.value && $.fn.DataTable.isDataTable(emailLogsTable.value)) {
          dataTable.destroy()
          dataTable = null
        }
      } catch (e) { /* ignore */ }
      nextTick(() => {
        try {
          if (!emailLogsTable.value) return
          if ($.fn.DataTable.isDataTable(emailLogsTable.value)) return
          dataTable = $(emailLogsTable.value).DataTable({
            dom: 'Bfrtip',
            responsive: true,
            paging: false,
            ordering: true,
            order: [[1, 'desc']],
            info: false,
            buttons: [
              'colvis',
              { extend: 'csv', text: 'Export CSV', className: 'btn btn-primary btn-sm' },
              { extend: 'excel', text: 'Export Excel', className: 'btn btn-primary btn-sm' },
              { extend: 'print', text: 'Print', className: 'btn btn-secondary btn-sm' },
            ],
          })
        } catch (err) {
          console.warn('DataTable init failed', err)
        }
      })
    }

    const destroyDataTable = () => {
      try {
        if (emailLogsTable.value && dataTable && $.fn.DataTable.isDataTable(emailLogsTable.value)) {
          dataTable.destroy()
          dataTable = null
        }
      } catch (e) { /* ignore */ }
    }

    const onTableClick = (e) => {
      const btn = e.target.closest('[data-action]')
      if (!btn || btn.disabled) return
      const id = parseInt(btn.dataset.id, 10)
      if (!id) return
      const log = logs.value.find(l => l.id === id)
      if (!log) return
      const action = btn.dataset.action
      if (action === 'preview') previewLog(log)
      else if (action === 'resend') resend(log)
    }

    const debouncedFetch = debounce(() => fetchLogs(1), 400)

    const goToPage = (p) => { currentPage.value = p; fetchLogs(p) }

    const clearFilters = () => {
      filters.tenderSearch = ''
      filters.type         = ''
      filters.status       = ''
      fetchLogs(1)
    }

    const resend = async (log) => {
      if (!log) return
      resendingId.value = log.id
      try {
        const token = getToken()
        const res  = await fetch(`${API_BASE}/commication/email-logs/${log.id}/resend`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        })
        const data = await res.json()
        if (!res.ok) throw new Error(data?.message || 'Resend failed')
        alert(`Email resent successfully to ${log.recipient}`)
        fetchLogs()
      } catch (err) {
        alert('Error: ' + err.message)
      } finally {
        resendingId.value = null
      }
    }

    const hasUsersAddPermission = () => {
      try {
        const p = localStorage.getItem('permission')
        const perm = p ? JSON.parse(p) : {}
        return !!perm?.usersAdd
      } catch (e) {
        return false
      }
    }

    const maskSubmitQuotationLink = (html) => {
      if (!html || hasUsersAddPermission()) return html
      return html.replace(
        /<a\s[^>]*>[\s\S]*?Submit Quotation[\s\S]*?<\/a>/gi,
        '<span style="color:#9ca3af;font-style:italic;">[Link hidden — permission required]</span>'
      )
    }

    const previewLog = (log) => {
      previewLog_.value    = log
      previewSubject.value = log.subject
      const rawHtml = log.htmlBody || '<p style="padding:16px;color:#666;">No HTML body stored for this log entry.</p>'
      previewHtml.value    = maskSubmitQuotationLink(rawHtml)
    }

    const formatDate = (d) => {
      if (!d) return '—'
      return new Date(d).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })
    }

    const formatType = (type) => {
      const map = {
        TENDER_INVITATION:        'Invitation',
        AWARD_NOTIFICATION:       'Award',
        REJECTION_NOTIFICATION:   'Rejection',
        CONTRACT_EXPIRY_30_DAYS:  'Expiry 30d',
        CONTRACT_EXPIRY_14_DAYS:  'Expiry 14d',
        CONTRACT_EXPIRY_7_DAYS:   'Expiry 7d',
        CONTRACT_EXPIRY_1_DAY:    'Expiry 1d',
        CONTRACT_EXPIRY_EXPIRED:  'Expired',
        GENERAL:                  'General',
      }
      return map[type] || type
    }

    const typeBadgeStyle = (type) => {
      const colors = {
        TENDER_INVITATION:       'background:rgba(37,99,235,.12);color:#6b7280 !important',
        AWARD_NOTIFICATION:      'background:rgba(22,163,74,.12);color:#6b7280 !important',
        REJECTION_NOTIFICATION:  'background:rgba(220,38,38,.12);color:#6b7280 !important',
        CONTRACT_EXPIRY_30_DAYS: 'background:rgba(100,116,139,.12);color:#6b7280',
        CONTRACT_EXPIRY_14_DAYS: 'background:rgba(217,119,6,.12);color:#6b7280 !important',
        CONTRACT_EXPIRY_7_DAYS:  'background:rgba(234,88,12,.12);color:#6b7280',
        CONTRACT_EXPIRY_1_DAY:   'background:rgba(220,38,38,.12);color:#6b7280',
        CONTRACT_EXPIRY_EXPIRED: 'background:rgba(127,29,29,.12);color:#6b7280',
        GENERAL:                 'background:rgba(107,114,128,.12);color:#6b7280',
      }
      return colors[type] || 'background:rgba(107,114,128,.12);color:#6b7280'
    }

    onMounted(() => fetchLogs(1))
    onBeforeUnmount(destroyDataTable)

    return {
      logs, loading, total, totalPages, currentPage,
      sentCount, failedCount, resendingId,
      previewHtml, previewSubject, previewLog_, filters,
      pageRange, fetchLogs, debouncedFetch, goToPage, clearFilters,
      resend, previewLog, formatDate, formatType, typeBadgeStyle,
      emailLogsTable, onTableClick,
    }
  }
}
</script>

<style scoped>
.email-logs-filters {
  background: transparent;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
}
.email-logs-filters :deep(.form-control),
.email-logs-filters :deep(.form-select) {
  background: transparent;
  border-color: rgba(0,0,0,.12);
}
.email-logs-filters :deep(.form-control:focus),
.email-logs-filters :deep(.form-select:focus) {
  background: transparent;
  border-color: rgba(0,0,0,.25);
}
.email-logs-filters-row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 16px;
}
.email-logs-filter-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.email-logs-filter-group input,
.email-logs-filter-group select {
  min-width: 160px;
}
.email-logs-filter-actions {
  display: flex;
  align-items: flex-end;
}
.email-logs-filter-label {
  font-size: 11px;
  font-weight: 500;
  color: #6b7280;
}
.email-logs-stats {
  display: flex;
  gap: 11px;
  margin-top: 13px;
  padding-top: 13px;
  border-top: 1px solid rgba(0,0,0,.08);
  font-size: 11px;
}
.badge-subtle-total {
  background: rgba(107, 114, 128, .15);
  color: #6b7280 !important;
  font-weight: 500;
  text-transform: uppercase !important;
}
.badge-subtle-sent {
  background: rgba(34, 197, 94, .15);
  color: #6b7280 !important;
  font-weight: 500;
  text-transform: uppercase !important;
}
.badge-subtle-failed {
  background: rgba(239, 68, 68, .15);
  color: #6b7280 !important;
  font-weight: 500;
  text-transform: uppercase !important;
}
.email-logs-card {
  background: transparent;
  border-radius: 8px;
  padding: 16px;
  min-height: 200px;
}
.email-logs-loading {
  text-align: center;
  padding: 48px 16px;
  color: #6b7280;
  font-size: 13px;
}
.email-logs-page .email-logs-table-wrapper {
  overflow-x: hidden;
  overflow-y: visible;
}
.email-logs-page .email-logs-table-wrapper :deep(.dataTables_wrapper),
.email-logs-page .email-logs-table-wrapper :deep(.dataTables_wrapper .dt-scroll-body) {
  background: transparent !important;
}
.email-logs-page .email-logs-table-wrapper :deep(.dataTables_wrapper),
.email-logs-page .email-logs-table-wrapper :deep(.dataTables_scroll),
.email-logs-page .email-logs-table-wrapper :deep(.dataTables_scrollBody) {
  overflow-x: hidden !important;
}
.email-logs-page .email-logs-table-wrapper :deep(table.dataTable td:nth-child(4)),
.email-logs-page .email-logs-table-wrapper :deep(table.dataTable td:nth-child(5)) {
  word-break: break-word;
  max-width: 180px;
}
.email-logs-page .email-logs-table-wrapper :deep(.dt-buttons) {
  margin-bottom: 11px;
}
.email-logs-page .email-logs-table-wrapper :deep(table.dataTable) {
  border: 1px solid rgba(0,0,0,.08);
  border-radius: 6px;
  overflow: hidden;
}
.btn-xs {
  padding: 2px 6px;
  font-size: 11px;
  line-height: 1.4;
}
.btn-ghost {
  background: transparent;
  border: none;
  color: #6b7280;
}
.btn-ghost:hover:not(:disabled) {
  background: rgba(0,0,0,.06);
  color: #374151;
}
.btn-ghost:disabled {
  opacity: .5;
  cursor: not-allowed;
}
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}
.modal-content {
  background: #fff;
  border-radius: 8px;
  width: 95%;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0,0,0,.2);
}
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 13px 20px;
  border-bottom: 1px solid #e5e7eb;
}
.email-preview-title {
  font-size: 11px;
  font-weight: 600;
  margin: 0;
  flex: 1;
  min-width: 0;
  line-height: 1.4;
  word-break: break-word;
  padding-right: 11px;
}
.close-btn {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #666;
}
</style>
